import React from "react";
import {
  Box,
  Typography,
  Grid,
  Divider,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import AppModal from "../common/AppModal";
import DetailItem from "../common/DetailItem";
import { formatCurrency } from "../../utils/currencyUtils";
import AppCircularProgress from "../common/AppCircularProgress";
import { SaleResponseDto } from "../../shared/dtos/sale/SaleResponseDto";

interface ViewSaleModalProps {
  open: boolean;
  onClose: () => void;
  sale: SaleResponseDto | null;
  loading: boolean;
  error: string | null;
}

const ViewSaleModal: React.FC<ViewSaleModalProps> = ({
  open,
  onClose,
  sale,
  loading,
  error,
}): React.JSX.Element => {
  const renderContent = (): React.ReactNode => {
    if (loading) return <AppCircularProgress />;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (!sale) return null;

    return (
      <Box>
        <Grid container spacing={2}>
          <DetailItem label="Cashier" value={sale.cashierName} />
          <DetailItem label="Branch" value={sale.branchName} />
          <DetailItem
            label="Shift Started"
            value={new Date(sale.shiftStartTime).toLocaleString()}
          />
          <DetailItem
            label="Documents"
            value={`${sale.receiptCount} Receipts, ${sale.invoiceCount} Invoices, ${sale.creditNoteCount} Credit notes`}
          />
        </Grid>

        <Divider sx={{ my: 2 }}>
          <Typography variant="overline">Financial Summary</Typography>
        </Divider>
        <Grid container spacing={2}>
          <DetailItem
            label="Gross Sales"
            value={formatCurrency(sale.salesTotal)}
          />
          <DetailItem label="Total Tax" value={formatCurrency(sale.totalTax)} />
          <DetailItem
            label="Total Discount"
            value={formatCurrency(sale.totalDiscount)}
          />
          <Grid {...({ item: true, xs: 12, sm: 6 } as Record<string, unknown>)}>
            <Typography variant="body2" color="text.secondary">
              Net Profit
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontWeight: "bold", color: "success.main" }}
            >
              {formatCurrency(sale.salesProfit)}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }}>
          <Typography variant="overline">Items Sold</Typography>
        </Divider>
        <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Product</TableCell>
                <TableCell align="right">Qty</TableCell>
                <TableCell align="right">Price</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="right">Profit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sale.items?.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                      {item.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.brand} | {item.category}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{item.qty}</TableCell>
                  <TableCell align="right">
                    {formatCurrency(item.price)}
                  </TableCell>
                  <TableCell align="right">
                    {formatCurrency(item.totalPrice)}
                  </TableCell>
                  <TableCell align="right" sx={{ color: "success.main" }}>
                    {formatCurrency(item.totalProfit)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Box>
    );
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title="Sale Performance Details"
      width="md"
    >
      {renderContent()}
    </AppModal>
  );
};

export default ViewSaleModal;
